/**
 * wayai report create — submit a platform bug report to the triage queue.
 *
 * Usage:
 *   wayai report create --title "Title" --description "Description"
 *   wayai report create -t "Title" -d "Description" --hub <uuid> --error "Error msg"
 *
 * Auto-captures hub/org context from .wayai.yaml if present.
 *
 * The report lands in the platform_report queue (status=pending). A Claude
 * Code Desktop routine triages on a 15-min cadence: groups by root cause,
 * dismisses noise, escalates actionable clusters by opening one GitHub issue
 * per group. You'll receive a follow-up email on escalation, on resolution
 * (fix shipped), or on dismissal (polite-no with the reason). If your report
 * is silently grouped as a duplicate of another, you won't get a separate
 * email — you'll only hear from us when the underlying issue is resolved.
 */

import { readFileSync } from 'node:fs';
import { dirname, join } from 'node:path';
import { fileURLToPath } from 'node:url';
import { requireAuth } from '../lib/auth.js';
import { readRepoConfig } from '../lib/repo-config.js';
import { ApiClient, ApiError } from '../lib/api-client.js';

const __dirname = dirname(fileURLToPath(import.meta.url));

function getCliVersion(): string {
  try {
    const pkg = JSON.parse(readFileSync(join(__dirname, '..', '..', 'package.json'), 'utf-8'));
    return `cli@${pkg.version}`;
  } catch {
    return 'cli@unknown';
  }
}

export async function reportCreateCommand(args: string[]): Promise<void> {
  // Parse args
  let title: string | undefined;
  let description: string | undefined;
  let source: string | undefined;
  let classification: string | undefined;
  let dedupKey: string | undefined;
  let hubId: string | undefined;
  let conversationId: string | undefined;
  let errorMessage: string | undefined;
  let steps: string | undefined;
  let context: string | undefined;

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--title' || arg === '-t') {
      title = args[++i];
    } else if (arg === '--description' || arg === '-d') {
      description = args[++i];
    } else if (arg === '--source') {
      source = args[++i];
    } else if (arg === '--classification') {
      classification = args[++i];
    } else if (arg === '--dedup-key') {
      dedupKey = args[++i];
    } else if (arg === '--hub') {
      hubId = args[++i];
    } else if (arg === '--conversation') {
      conversationId = args[++i];
    } else if (arg === '--error' || arg === '-e') {
      errorMessage = args[++i];
    } else if (arg === '--steps' || arg === '-s') {
      steps = args[++i];
    } else if (arg === '--context') {
      context = args[++i];
    }
  }

  if (!title || !description) {
    console.error('Usage: wayai report create --title "Title" --description "Description"');
    console.error('');
    console.error('Required:');
    console.error('  -t, --title <text>         Bug title');
    console.error('  -d, --description <text>   Detailed description');
    console.error('');
    console.error('Optional:');
    console.error('  --source <value>           Origin: cli (default) | review | security_audit');
    console.error('  --classification <value>   Nature: bug (default) | flaky_test | security');
    console.error('  --dedup-key <text>         Stable dedup key (e.g. "<file>::<test>")');
    console.error('  --hub <uuid>               Hub ID (optional context for the bug report)');
    console.error('  --conversation <uuid>      Conversation ID');
    console.error('  -e, --error <text>         Error message or stack trace');
    console.error('  -s, --steps <text>         Steps to reproduce');
    console.error('  --context <text>           Additional context');
    process.exit(1);
  }

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  // Auto-capture from .wayai.yaml (optional — don't fail if missing)
  const repoConfig = readRepoConfig();
  const organizationId = repoConfig?.organization_id;

  try {
    const result = await client.submitReport({
      title,
      description,
      source,
      classification,
      dedup_key: dedupKey,
      organization_id: organizationId,
      hub_id: hubId,
      conversation_id: conversationId,
      error_message: errorMessage,
      steps_to_reproduce: steps,
      additional_context: context,
      client_version: getCliVersion(),
    });

    if (result.created) {
      console.log(`Report submitted: ${result.report_id}`);
    } else {
      console.log(`Report merged with existing: ${result.report_id}`);
    }
    console.log('A WayAI engineer (or one of our AI agents) will triage your report.');
    console.log('You\'ll get an email on escalation, resolution, or dismissal — if your');
    console.log('report is silently grouped as a duplicate, you won\'t get a separate email.');
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 429) {
        console.error('Rate limit exceeded. Please try again later.');
      } else if (err.status === 503) {
        console.error('Report submission is not currently available.');
      } else {
        console.error(`Failed to submit report: ${err.message}`);
      }
      process.exit(1);
    }
    throw err;
  }
}
